<template>
    <PageErrorFourHundredFour v-if="return404" />

    <div
        v-else
        class="ready-to-go-rings"
    >
        <PageContentLoader v-if="fetching && !data" />

        <PageErrorFiveHundred
            v-else-if="error"
            :errors="responseErrors"
        >
            <ThButton
                :loading="fetching"
                @click="executeQuery"
            >
                {{ t('globals.try_again') }}
            </ThButton>
        </PageErrorFiveHundred>

        <div v-else-if="data">
            <ProductBreadcrumbs :breadcrumbs />

            <div v-qa="'before-filters-content'">
                <CmsBlocks
                    :data="dataContent?.pageProductListing?.beforeFilters || []"
                />
            </div>

            <div class="catalogue-switch-wrapper">
                <ThLink
                    :to="routes.engagementRings({})"
                    class="catalogue-switch"
                >
                    Want to see more?
                    <span class="highlighted">View our full collection</span>
                </ThLink>
            </div>

            <ProductGrid
                :banner1="dataContent?.pageProductListing?.banner1 || undefined"
                :banner2="dataContent?.pageProductListing?.banner2 || undefined"
                :data="data.catalogueReadyToGoRings"
                :fetching
                :full-grid-at="perPage"
                :get-route-sort="getRouteSort"
                :page
                :results-count="data.catalogueReadyToGoRingsAggregate.count"
                :sort-by="sortByWithDefault"
                :filter-chips
                :route-reset
                is-r-t-g
                :track-clicked
                :active-filter-count
                @open-filters-modal="openFilterModal"
            />

            <Pagination
                v-if="data.catalogueReadyToGoRingsAggregate.count > 0"
                :count="data.catalogueReadyToGoRingsAggregate.count"
                :current="page"
                :get-route-page="getRoutePage"
                :limit="perPage"
                :range="2"
                scroll-into-view-selector=".product-grid"
            />

            <CmsBlocks
                :data="dataContent?.pageProductListing?.afterGrid || []"
            />
        </div>

        <ReadyToGoRingFilters
            v-if="
                data &&
                data.catalogueReadyToGoRingsFilters &&
                !error &&
                isFilterModalRendered
            "
            v-model:values="filterInput"
            v-qa="'ready-to-go-ring-filters'"
            :filters="data.catalogueReadyToGoRingsFilters"
            :get-price-route="getRoutePrice"
            :get-route-add-metal="getRouteAddMetal"
            :get-route-remove-metal="getRouteRemoveMetal"
            :is-open="isFilterModalOpen"
            :route-reset
            :chips="filterChips"
            :result-count="data.catalogueReadyToGoRingsAggregate.count"
            :loading="fetching"
            @close="closeFilterModal"
        />
    </div>
</template>

<script lang="ts" setup>
import { computed, defineAsyncComponent, hydrateOnVisible, toRef } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'

import { CatalogueSortOrder, type MetalCode } from '@/graphql/api'
import { useTranslator } from '@/i18n'
import { useRoutes } from '@/router/routes'
import { useMeta } from '@/composables/app/useMeta'
import { useResponseError } from '@/helpers/errors'
import { useRegionPlugin } from '@/plugins/region'
import { useIsAdmin } from '@/components/app/isAdmin'
import { useProductListingPage } from '@/views/product/useProductListingPage'
import { useProductListAnalytics } from '../composables/analytics/useProductListAnalytics'
import { useReadyToGoRingsFilters } from './useReadyToGoRingsFilters'
import type { Breadcrumb } from '@/components/ui/Breadcrumbs.vue'
import { useCurrencyInput } from '@/plugins/currency-input.ts'
import { usePageCount } from '@/composables/page-count.ts'
import { usePageWatcher } from '@/composables/page-watcher.ts'
import { useFilterModal } from '@/composables/filter-modal.ts'
import {
    useReadyToGoRingBrowseQuery,
    useReadyToGoRingsContentQuery,
} from '@/views/product/ready-to-go-ring/ReadyToGoRings.generated.ts'

import ThButton from '@/components/ui/buttons/ThButton.vue'
import PageErrorFiveHundred from '@/components/PageErrorFiveHundred.vue'
import ProductGrid from '@/components/rings/product-grid/ProductGrid.vue'
import ThLink from '@/components/ui/buttons/ThLink.vue'
import ProductBreadcrumbs from '@/components/rings/ProductBreadcrumbs.vue'
import PageContentLoader from '@/components/ui/PageContentLoader.vue'
import { useFilterButtonClient } from '@/components/product/filter-button/useFilterButton'

const ReadyToGoRingFilters = defineAsyncComponent({
    loader: () =>
        import('@/components/rings/ready-to-go/ReadyToGoRingFilters.vue'),
    hydrate: hydrateOnVisible(),
})

const PageErrorFourHundredFour = defineAsyncComponent({
    loader: () => import('@/components/PageErrorFourHundredFour.vue'),
    hydrate: hydrateOnVisible(),
})

const Pagination = defineAsyncComponent({
    loader: () => import('@/components/ui/Pagination.vue'),
    hydrate: hydrateOnVisible(),
})

const CmsBlocks = defineAsyncComponent({
    loader: () => import('@/components/cms/CmsBlocks.vue'),
    hydrate: hydrateOnVisible(),
})

export interface Props {
    slug: string[]
    metal?: MetalCode
    page: number
    priceMin?: number
    priceMax?: number
    readyToShip?: boolean
    sortBy?: CatalogueSortOrder
}

const props = withDefaults(defineProps<Props>(), {
    metal: undefined,
    priceMin: undefined,
    priceMax: undefined,
    readyToShip: false,
    sortBy: undefined,
})

const sortByWithDefault = toRef(
    () => props.sortBy ?? CatalogueSortOrder.Recommended
)

const perPage = 32

const isAdmin = useIsAdmin()

const return404 = computed(() => props.readyToShip && !isAdmin.value)

const { regionCode } = useRegionPlugin()
const { locale } = useTranslator()
const currencyInput = useCurrencyInput()

const router = useRouter()
const routes = useRoutes()
const { t } = useI18n()
const {
    filterInput,
    routeReset,
    getRoutePrice,
    getRouteAddMetal,
    getRouteRemoveMetal,
    getRouteSort,
    activeFilterCount,
    filterChips,
} = useReadyToGoRingsFilters(
    props,
    toRef(() => data.value?.catalogueReadyToGoRingsFilters)
)

const {
    isFilterModalOpen,
    isFilterModalRendered,
    openFilterModal,
    closeFilterModal,
} = useFilterModal()

useFilterButtonClient(openFilterModal, activeFilterCount)

const responseContent = useReadyToGoRingsContentQuery({
    variables: computed(() => ({
        path: 'ready-to-go',
        locale: locale.value,
        currency: currencyInput.value,
        region: regionCode.value,
    })),
})

// TODO use when bug is fixed
// const { gridNode } = useFocusFilterButton(isFiltersModalOpen)

const response = useReadyToGoRingBrowseQuery({
    variables: computed(() => ({
        filter: filterInput.value,
        limit: perPage,
        currency: currencyInput.value,
        region: regionCode.value,
        page: props.page,
        locale: locale.value,
        sortBy: sortByWithDefault.value,
    })),
})

function getRoutePage(p: number) {
    return routes.readyToGoRings(
        props.metal,
        props.priceMin,
        props.priceMax,
        p,
        props.readyToShip
    )
}

const pageCount = usePageCount(
    () => response.data.value?.catalogueReadyToGoRingsAggregate.count ?? 0,
    () => perPage
)
const pageWatcher = usePageWatcher(
    () => props.page,
    pageCount,
    () => {
        return router.resolve(getRoutePage(1)).fullPath
    },
    302
)

await response
await responseContent

const responseErrors = useResponseError(response)

const { fetching, stale, data, error, executeQuery } = response
const { data: dataContent } = responseContent

const catalogue = toRef(() => data.value?.catalogueReadyToGoRings)

const facets = computed(() => ({
    metal: data.value?.catalogueReadyToGoRingsFilters.primaryMetals.find(
        (v) => v.metal.systemCode === props.metal
    ),
}))

const { trackClicked } = useProductListAnalytics(
    catalogue,
    filterInput,
    computed(() => [facets.value.metal?.metal.translation.name]),
    sortByWithDefault,
    toRef(() => props.page)
)

const { breadcrumbs } = useProductListingPage({
    facets,
    resetRouteGetter: (facets) => {
        return routes.readyToGoRings(
            facets['metal']?.metal?.systemCode,
            undefined,
            undefined,
            undefined,
            props.readyToShip
        )
    },
    breadcrumbsGetter: (facets) => {
        const home: Breadcrumb = {
            title: t('globals.home'),
            to: routes.home(),
        }
        const rings: Breadcrumb = {
            title: t('catalogue.ready_to_go.title'),
            to: routes.readyToGoRings(),
        }

        return computed<Breadcrumb[]>(() => {
            const baseBreadcrumbs: Breadcrumb[] = [home, rings]

            const metalValue = facets.value.metal?.metal
            if (metalValue) {
                baseBreadcrumbs.push({
                    title: metalValue.translation.name,
                })
            }

            return baseBreadcrumbs
        })
    },
})

useMeta(
    {
        title: () => dataContent.value?.pageProductListing?.meta.title,
        description: () =>
            dataContent.value?.pageProductListing?.meta.description,
    },
    data,
    fetching,
    stale
)

await pageWatcher()
</script>

<style lang="scss" scoped>
.ready-to-go-rings {
    .catalogue-switch-wrapper {
        margin: 20px 0 0;
        display: flex;
        justify-content: center;

        .catalogue-switch {
            padding: 10px 20px;
            background-color: $stone-2;
            align-self: center;
            border-radius: 60px;
            font-size: 0.75rem;
            line-height: 1.25rem;

            @include min-width(640) {
                padding: 10px 30px;
                font-size: 0.875rem;
            }

            .highlighted {
                font-family: $effra-font-stack;
                font-size: 0.675rem;
                font-weight: 400;
                text-transform: uppercase;
                letter-spacing: 0.05em;
                color: $blue;

                @include min-width(640) {
                    font-size: 0.75rem;
                }
            }
        }
    }
}
</style>
